/**
 * SFDA-858: /welcome and /callback with REACT_APP_AUTH_PROVIDER=zitadel.
 * Signing in happens on our login page (apps/login, via Zitadel); these pages only send
 * people there and take them back.
 */
import { Box, Button, CssBaseline, Typography } from '@mui/material';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { PageTitle } from '../../../components/PageTitle';
import { useAuth } from '../../../contexts/auth/useAuth';
import {
  NoAccountError,
  useZitadel,
} from '../../../contexts/auth/zitadelContext';
import { safeReturnTo } from '../welcome/Welcome';

/** A centred card with one heading that takes focus, so screen readers start there. */
function Shell({
  heading,
  children,
}: {
  heading: string;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, [heading]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--color-light-greyish-blue)',
      }}
    >
      <CssBaseline />
      <PageTitle title={t('auth.welcome.pageTitle')} />
      <Box
        component="main"
        id="main-content"
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 16,
          py: 32,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            p: 32,
            maxWidth: 480,
            width: '100%',
            backgroundColor: 'var(--color-white, #fff)',
            borderRadius: 2,
          }}
        >
          <Typography
            component="h1"
            variant="h4"
            ref={headingRef}
            tabIndex={-1}
            sx={{ outline: 'none' }}
          >
            {heading}
          </Typography>
          {children}
        </Box>
      </Box>
    </Box>
  );
}

/** Shown while the stored session is checked (and while the provider loads): a status, not a blank page. */
export function ZitadelLoading() {
  const { t } = useTranslation();
  return (
    <Box
      component="main"
      id="main-content"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* The page's only text, so also its h1 (axe page-has-heading-one), inside the status region. */}
      <Box role="status">
        <Typography component="h1" variant="body1">
          {t('auth.zitadel.loading')}
        </Typography>
      </Box>
    </Box>
  );
}

export function ZitadelWelcome() {
  const { t } = useTranslation();
  const { isAuthenticated, sessionExpired } = useAuth();
  const { signIn } = useZitadel();
  const [searchParams] = useSearchParams();
  const returnTo = safeReturnTo(searchParams.get('returnTo'));
  const [failed, setFailed] = useState(false);
  const started = useRef(false);

  const go = () => {
    setFailed(false);
    signIn(returnTo).catch(() => setFailed(true));
  };

  // Straight on to sign-in, except after an expired session: say so first.
  useEffect(() => {
    if (isAuthenticated || sessionExpired || started.current) return;
    started.current = true;
    go();
  }, [isAuthenticated, sessionExpired]);

  if (isAuthenticated) return <Navigate to={returnTo} replace />;

  if (sessionExpired || failed) {
    return (
      <Shell
        heading={
          failed
            ? t('auth.zitadel.unreachable')
            : t('auth.welcome.errors.session_expired')
        }
      >
        <Button variant="primary" onClick={go}>
          {t('auth.signIn')}
        </Button>
      </Shell>
    );
  }

  return (
    <Shell heading={t('auth.welcome.pageTitle')}>
      <Typography role="status">{t('auth.zitadel.redirecting')}</Typography>
    </Shell>
  );
}

export function ZitadelCallback() {
  const { t } = useTranslation();
  const { completeSignIn } = useZitadel();
  const navigate = useNavigate();
  const [error, setError] = useState<'noAccess' | 'failed' | null>(null);
  const started = useRef(false);

  useEffect(() => {
    // The code can be exchanged once; StrictMode runs effects twice in dev.
    if (started.current) return;
    started.current = true;
    completeSignIn()
      .then((returnTo) => navigate(safeReturnTo(returnTo), { replace: true }))
      .catch((err) =>
        setError(err instanceof NoAccountError ? 'noAccess' : 'failed')
      );
  }, [completeSignIn, navigate]);

  if (error === 'noAccess') {
    return (
      <Shell heading={t('auth.welcome.noAccess.heading')}>
        <Typography>{t('auth.welcome.noAccess.message')}</Typography>
        <Button variant="primary" onClick={() => navigate('/welcome')}>
          {t('auth.welcome.noAccess.differentEmail')}
        </Button>
      </Shell>
    );
  }
  if (error === 'failed') {
    return (
      <Shell heading={t('auth.zitadel.failed')}>
        <Button variant="primary" onClick={() => navigate('/welcome')}>
          {t('auth.zitadel.retry')}
        </Button>
      </Shell>
    );
  }
  return (
    <Shell heading={t('auth.welcome.pageTitle')}>
      <Typography role="status">{t('auth.zitadel.signingIn')}</Typography>
    </Shell>
  );
}
